import { useTranslation } from 'react-i18next'

import type { ColorScheme } from '@hivewatch/dark'

import { FilterDrawer } from '@/components/filter-drawer'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { removeNullValues } from '@/redux/incidents/utils'
import {
  selectSOPCreatedAtFilter,
  selectSOPCreatorIdFilter,
  selectSOPFacilityIdFilter,
  selectSOPTypeFilter,
  setSOPCreatedAtFilter,
  setSOPCreatorIdFilter,
  setSOPFacilityIdFilter,
  setSOPTypeFilter,
} from '@/redux/ui/uiSlice'
import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'

import type { SOPsFilterFormInputsI } from '../types/form'
import { DarkSOPsFilterForm } from './DarkSOPsFilterForm'

const FILTER_FIELD_NAMES: Array<keyof SOPsFilterFormInputsI> = [
  'facilityId',
  'createdAt',
  'creator',
  'type',
]

interface DarkSOPsFilterDrawerIProps {
  isOpen: boolean
  onClose: () => void
  colorScheme: ColorScheme
}

export const DarkSOPsFilterDrawer = ({
  isOpen,
  onClose,
  colorScheme,
}: DarkSOPsFilterDrawerIProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const facilityIdFilterValue = useAppSelector(selectSOPFacilityIdFilter)
  const createdAtFilterValue = useAppSelector(selectSOPCreatedAtFilter)
  const creatorFilterValue = useAppSelector(selectSOPCreatorIdFilter)
  const typeFilterValue = useAppSelector(selectSOPTypeFilter)

  const defaultValues: SOPsFilterFormInputsI = {
    facilityId: facilityIdFilterValue ?? null,
    createdAt: createdAtFilterValue ?? null,
    creator: creatorFilterValue ?? null,
    type: typeFilterValue ?? null,
  }

  const onApply = (values: SOPsFilterFormInputsI) => {
    dispatch(setSOPFacilityIdFilter(values.facilityId ?? null))
    dispatch(setSOPCreatedAtFilter(values.createdAt ?? null))
    dispatch(setSOPCreatorIdFilter(values.creator ?? null))
    dispatch(setSOPTypeFilter(values.type ?? null))

    trackEvent('SOPs View: Filters Applied', {
      filters: removeNullValues({
        facilityId: values.facilityId,
        createdAt: values.createdAt,
        creator: values.creator,
        type: values.type,
      }),
    })
  }

  return (
    <FilterDrawer<SOPsFilterFormInputsI>
      colorScheme={colorScheme}
      defaultValues={defaultValues}
      fieldNames={FILTER_FIELD_NAMES}
      isOpen={isOpen}
      onApply={onApply}
      onClear={() => trackEvent(`${BUTTON_PRESS} SOPs Drawer - Clear Filters`)}
      onClose={onClose}
      title={t('sops:table_filter_title')}
    >
      {(methods) => (
        <DarkSOPsFilterForm
          colorScheme={colorScheme}
          control={methods.control}
        />
      )}
    </FilterDrawer>
  )
}
